import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import {
  MASS_OS_MONTH_OPTIONS,
  MASS_OS_YEAR_OPTIONS,
  MassOsGenerationForm,
  MassOsPeriodMode,
  MassOsProviderOption
} from 'src/entities/file';

type ProviderPeriodPatch = Partial<
  Pick<
    MassOsGenerationForm,
    | 'providerId'
    | 'periodMode'
    | 'year'
    | 'months'
    | 'rangeStart'
    | 'rangeEnd'
  >
>;

@Component({
  selector: 'app-mass-os-provider-period',
  templateUrl: './mass-os-provider-period.component.html',
  styleUrls: ['./mass-os-provider-period.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MassOsProviderPeriodComponent {
  @Input() providers: MassOsProviderOption[] = [];
  @Input() providerId: string | null = null;
  @Input() periodMode: MassOsPeriodMode = 'month';
  @Input() year: number | null = null;
  @Input() months: number[] = [];
  @Input() rangeStart: Date | null = null;
  @Input() rangeEnd: Date | null = null;
  @Input() submitted = false;
  @Input() isLoadingProviders = false;

  @Output() formChange = new EventEmitter<ProviderPeriodPatch>();

  @ViewChild('providerDropdown') dropdownRef!: ElementRef<HTMLElement>;

  readonly years = MASS_OS_YEAR_OPTIONS;
  readonly monthsList = MASS_OS_MONTH_OPTIONS;
  providerSearch = '';
  providerDropdownOpen = false;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.providerDropdownOpen) {
      return;
    }
    const target = event.target as HTMLElement;
    if (this.dropdownRef?.nativeElement?.contains(target)) {
      return;
    }
    this.providerDropdownOpen = false;
    this.providerSearch = '';
    this.cdr.markForCheck();
  }

  get filteredProviders(): MassOsProviderOption[] {
    const term = this.providerSearch.trim().toLowerCase();
    if (!term) {
      return this.providers;
    }
    return this.providers.filter((p) =>
      p.name.toLowerCase().includes(term)
    );
  }

  get selectedProvider(): MassOsProviderOption | undefined {
    return this.providers.find((provider) => provider.id === this.providerId);
  }

  get isAllYear(): boolean {
    return this.months.length === this.monthsList.length;
  }

  get providerError(): string | null {
    return this.submitted && !this.providerId
      ? 'Proveedor es requerido'
      : null;
  }

  get rangeStartError(): string | null {
    return this.periodMode === 'range' && this.submitted && !this.rangeStart
      ? 'Seleccionar fecha de inicio es requerido'
      : null;
  }

  get rangeEndError(): string | null {
    return this.periodMode === 'range' && this.submitted && !this.rangeEnd
      ? 'Seleccionar fecha de término es requerido'
      : null;
  }

  get monthsError(): string | null {
    return this.periodMode === 'month' &&
      this.submitted &&
      (this.year === null || !this.months.length)
      ? 'Periodo de órdenes de servicio es requerido'
      : null;
  }

  onProviderChange(providerId: string): void {
    this.formChange.emit({ providerId });
    this.providerDropdownOpen = false;
    this.providerSearch = '';
    this.cdr.markForCheck();
  }

  toggleProviderDropdown(): void {
    this.providerDropdownOpen = !this.providerDropdownOpen;
    if (!this.providerDropdownOpen) {
      this.providerSearch = '';
    }
    this.cdr.markForCheck();
  }

  closeProviderDropdown(): void {
    this.providerDropdownOpen = false;
    this.providerSearch = '';
    this.cdr.markForCheck();
  }

  onPeriodModeChange(periodMode: MassOsPeriodMode): void {
    this.formChange.emit({ periodMode });
  }

  onYearChange(year: number): void {
    this.formChange.emit({ year: this.year === year ? null : year });
  }

  toggleMonth(month: number): void {
    const months = this.months.includes(month)
      ? this.months.filter((value) => value !== month)
      : [...this.months, month].sort((a, b) => a - b);
    this.formChange.emit({ months });
  }

  toggleAllYear(): void {
    const months = this.isAllYear
      ? []
      : this.monthsList.map((month) => month.value);
    this.formChange.emit({ months });
  }

  onRangeStartChange(value: Date | null): void {
    this.formChange.emit({ rangeStart: value });
  }

  onRangeEndChange(value: Date | null): void {
    this.formChange.emit({ rangeEnd: value });
  }
}
